<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { customerInfoProviderContextToken } from "@/context_tokens";
    import checkIsMobile from "@/utils/checkIsMobile";
    import WishlistItemEditableOptions from "@apps/wishlist/components/WishlistItemEditableOptions.svelte";
    import WishlistItemOptions from "@apps/wishlist/components/WishlistItemOptions.svelte";
    import WishlistSocialShare from "@apps/wishlist/components/WishlistSocialShare/WishlistSocialShare.svelte";
    import { BUTTON_VARIANT } from "@apps/wishlist/components/WishlistTheme/constants";
    import WishlistTheme from "@apps/wishlist/components/WishlistTheme/WishlistTheme.svelte";
    import {
        SAVE_WISHLIST_MODAL_ACTION_TYPES,
        WISHLIST_EMAIL_PARAM_TOKEN,
        WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES,
    } from "@apps/wishlist/constants/constants";
    import { SaveWishlistModalWidgetDefinition } from "@apps/wishlist/definitions";
    import { ShareWishlistsQuery } from "@apps/wishlist/queries/ShareWishlistsQuery";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import WishlistsBackInStockItemsQuery from "@apps/wishlist/queries/WishlistsBackInStockItemsQuery";
    import { WishlistsQuery } from "@apps/wishlist/queries/WishlistsQuery";
    import { ShareWishlistsService } from "@apps/wishlist/services/ShareWishlistsService";
    import WishlistsBackInStockItemsService from "@apps/wishlist/services/WishlistsBackInStockItemsService";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
    import { saveWishlistModalToken } from "@apps/wishlist/tokens";
    import {
        WISHLIST_ITEMS_FILTERING_TYPE,
        WISHLIST_ITEMS_SORTING_TYPE,
    } from "@apps/wishlist/types";
    import WishlistFilterManager from "@apps/wishlist/utils/WishlistFilterManager";
    import WishlistSortingManager from "@apps/wishlist/utils/WishlistSortingManager";
    import WishlistPageList from "@apps/wishlist/widgets/wishlist_page_widget/components/WishlistPageList.svelte";
    import WishlistPageListSkeleton from "@apps/wishlist/widgets/wishlist_page_widget/components/WishlistPageListSkeleton.svelte";
    import ClipboardInterface from "@lib/DOMClipboardAdapter/ClipboardInterface";
    import DOMClipboardAdapter from "@lib/DOMClipboardAdapter/DOMClipboardAdapter";
    import {
        getTypedContext,
        setTypedContext,
    } from "@lib/svelte-typed-context";
    import WindowPopupManager from "@lib/WindowPopupManager/WindowPopupManager";
    import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import { CartQuery } from "@modules/cart/queries/CartQuery";
    import { CartService } from "@modules/cart/services/CartService";
    import CartAvailabilityProvider from "@modules/common_module/CartAvailabilityProvider/CartAvailabilityProvider";
    import { CommonModule } from "@modules/common_module/CommonModule";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import GrowaveStorefrontValuesService from "@modules/growave_storefront_values/services/GrowaveStorefrontValuesService";
    import { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import { CombinedProductsAndProductsWithQuantityRulesQuery } from "@modules/products/queries/CombinedProductsAndProductsWithQuantityRulesQuery";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import { ProductsWithQuantityRulesQuery } from "@modules/products/queries/ProductsWithQuantityRulesQuery";
    import Observable from "@ui-utils/Observable.svelte";
    import {
        Subscription,
        filter,
        lastValueFrom,
        map,
        switchMap,
        take,
    } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import Button from "@components/Buttons/Button.svelte";
    import GrowaveBrand from "@components/GrowaveBrand/GrowaveBrand.svelte";
    import Icon from "@components/Icon.svelte";
    import PlusIcon from "@components/icons/PlusIcon.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import Sort from "@components/Sort.svelte";
    import T from "@components/T.svelte";

    import SaveWishlistButton from "../save_wishlist_modal_widget/components/SaveWishlistButton.svelte";
    import { OpenedWishlistsUIStore } from "../wishlists_widget/ui_stores/OpenedWishlistsUIStore";
    import WishlistPageItem from "./components/WishlistPageItem.svelte";
    import { wishlistPageWidgetContext } from "./WishlistPageWidgetContext";

    export let wishlistsService: WishlistsService;
    export let wishlistsQuery: WishlistsQuery;
    export let wishlistsBackInStockItemsService: WishlistsBackInStockItemsService;
    export let wishlistsBackInStockItemsQuery: WishlistsBackInStockItemsQuery;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;
    export let wishlistsStorefrontValuesService: WishlistsStorefrontValuesService;
    export let productsQuery: ProductsQuery;
    export let combinedProductsAndProductsWithQuantityRulesQuery: CombinedProductsAndProductsWithQuantityRulesQuery;
    export let productsWithQuantityRulesQuery: ProductsWithQuantityRulesQuery;
    export let cartService: CartService;
    export let cartQuery: CartQuery;
    export let modalManager: ModalManager;
    export let shareWishlistsService: ShareWishlistsService;
    export let shareWishlistsQuery: ShareWishlistsQuery;
    export let growaveStorefrontValuesService: GrowaveStorefrontValuesService;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let dynamicImportManager: DynamicImportManager;
    export let cartAvailabilityProvider: CartAvailabilityProvider;
    export let showTitle: boolean;
    export let wishlistItemOptionsEditable: boolean;
    export let gwI18n: GwI18n;
    export let windowPopupManager: WindowPopupManager;

    const wishlistBranding$ =
        wishlistsStorefrontValuesService.selectWishlistsBranding();
    const watermarkSettings$ =
        growaveStorefrontValuesService.selectWatermarkSettings();

    const customerInfoProvider = getTypedContext(
        customerInfoProviderContextToken
    );
    const isB2b = customerInfoProvider.getIsB2b();

    const openedWishlistsUIStore = new OpenedWishlistsUIStore();
    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));
    const clipboard: ClipboardInterface = new DOMClipboardAdapter();
    const locationController =
        container.resolve(CommonModule).exports.locationController;
    const searchParams = locationController.getSearchParams();

    const wishlists$ = wishlistsQuery.selectAll();
    const hasAnyItems$ = wishlistsQuery.hasAnyItems$;
    const settings$ =
        wishlistsStorefrontValuesService.selectWishlistsSettings();
    const enabledSocialShareChannels$ =
        wishlistsStorefrontValuesService.selectEnabledSocialShareChannels();
    const status$ = wishlistsQuery.status$;
    const loadMoreStatus$ = wishlistsQuery.loadMoreStatus$;

    const shareWishlistsStatus$ = shareWishlistsQuery.status$;
    const sendNotificationToApproversStatus$ =
        wishlistsQuery.sendNotificationToApproversStatus$;
    const createDraftOrderStatus$ = wishlistsQuery.createDraftOrderStatus$;
    const approveWishlistStatus$ = wishlistsQuery.approveWishlistStatus$;
    let isShareLinkCopied = false;

    const isMobile = checkIsMobile();
    const isCartAvailable = cartAvailabilityProvider.isCartAvailable();

    $: showSaveWishlistButton =
        $settings$?.isSaveWishlistEnabled && !$isCustomer$ && $hasAnyItems$;

    $: showSocialShareIcons =
        $settings$?.isWishlistSocialShareEnabled &&
        $hasAnyItems$ &&
        $enabledSocialShareChannels$.length;

    setTypedContext(wishlistPageWidgetContext, {
        cartService: cartService,
        cartQuery: cartQuery,
    });

    const wishlistSortingManager = new WishlistSortingManager({
        labelTranslationNamespace: "wishlist.critical.WishlistPageWidget",
    });
    const currentSort$ = wishlistSortingManager.selectCurrentSortValue();
    const sortItems = wishlistSortingManager.getSortItems();

    const wishlistFilterManager = new WishlistFilterManager({
        labelTranslationNamespace: "wishlist.critical.WishlistPageWidget",
    });
    const currentFilterValues$ =
        wishlistFilterManager.selectCurrentFilterValues();
    const filterItems = wishlistFilterManager.getFilterItems();

    let getWishlistsSubscription: Subscription | null = null;

    const mergeAndGetWishlists = async () => {
        const wishlistEmailParamToken = searchParams.get(
            WISHLIST_EMAIL_PARAM_TOKEN
        );

        if (wishlistEmailParamToken) {
            await lastValueFrom(
                wishlistsService.mergeWishlists(wishlistEmailParamToken)
            );
        }

        getWishlistsSubscription = currentSort$.subscribe((currentSort) => {
            lastValueFrom(
                wishlistsService.getWishlists({
                    orderBy: currentSort,
                })
            );
        });
    };

    onMount(async () => {
        mergeAndGetWishlists();
        cartService.getCart();

        wishlists$
            .pipe(
                map((wishlists) =>
                    wishlists.flatMap((wishlist) =>
                        wishlist.items.map((item) => ({
                            productId: item.productId,
                            variantId: item.variantId,
                            date: item.date,
                        }))
                    )
                ),
                filter((params) => params.length > 0),
                take(1),
                switchMap((params) =>
                    wishlistsBackInStockItemsService.getBackInStockWishlistItems(
                        params
                    )
                )
            )
            .subscribe();

        return () => {
            getWishlistsSubscription?.unsubscribe();
        };
    });

    const handleDeleteItem = (
        wishlistId: number,
        productId: number,
        variantId: number
    ) => {
        lastValueFrom(
            wishlistsService.deleteItem({ wishlistId, productId, variantId })
        );
    };

    const handleAddItem = (
        wishlistId: number,
        productId: number,
        variantId: number
    ) => {
        lastValueFrom(
            wishlistsService.addItem({ productId, variantId, wishlistId })
        );
    };

    const handleMoveItem = (
        wishlistId: number,
        productId: number,
        variantId: number,
        targetWishlistId: number
    ) => {
        lastValueFrom(
            wishlistsService.moveItem({
                wishlistId,
                productId,
                variantId,
                targetWishlistId,
            })
        );
    };

    const handleUpdateItemVariant = (
        event: CustomEvent<{
            wishlistId: number;
            productId: number;
            oldVariantId: number;
            newVariantId: number;
        }>
    ) => {
        lastValueFrom(
            wishlistsService.updateWishlistItemVariant({
                wishlistId: event.detail.wishlistId,
                productId: event.detail.productId,
                oldVariantId: event.detail.oldVariantId,
                newVariantId: event.detail.newVariantId,
            })
        );
    };

    const handleRenameWishlist = (e: CustomEvent) => {
        lastValueFrom(wishlistsService.renameWishlist(e.detail));
    };

    const handleMakeWishlistDefault = (e: CustomEvent) => {
        lastValueFrom(wishlistsService.makeWishlistDefault(e.detail));
    };

    const handleTogglePrivateWishlist = (e: CustomEvent) => {
        lastValueFrom(wishlistsService.togglePrivateWishlist(e.detail));
    };

    const handleDeleteWishlist = (e: CustomEvent) => {
        lastValueFrom(wishlistsService.deleteWishlist(e.detail));
    };

    const handleDuplicateWishlist = (e: CustomEvent) => {
        lastValueFrom(wishlistsService.duplicateWishlist(e.detail));
    };

    const handleLoadMore = (e: CustomEvent) => {
        lastValueFrom(wishlistsService.getWishlistItems(e.detail));
    };

    const createWishlist = (title: string) => {
        lastValueFrom(
            wishlistsService.createWishlist({
                title,
            })
        );
    };

    const handleOpenSaveModal = async () => {
        await dynamicImportManager.fetch({
            definition: SaveWishlistModalWidgetDefinition,
        });
        // Ensure the modal widget initializes asynchronously before opening it
        setTimeout(() => {
            modalManager.openModal(saveWishlistModalToken, undefined, {
                actionType: SAVE_WISHLIST_MODAL_ACTION_TYPES.saveWishlists,
            });
        }, 0);
    };

    const changeShareLinkCopyStatus = () => {
        isShareLinkCopied = true;
        setTimeout(() => (isShareLinkCopied = false), 800);
    };

    const handleShareWishlists = (
        channelName: WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES,
        wishlistIds: number[],
        linkCopiedCallback: () => void
    ) => {
        if (channelName === WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES.copyShareLink) {
            handleCopyShareLink(channelName, wishlistIds, linkCopiedCallback);
        } else {
            handleOpenPopup(channelName, wishlistIds);
        }
    };

    const handleCopyShareLink = (
        channelName: WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES,
        wishlistIds: number[],
        linkCopiedCallback: () => void
    ) => {
        // safari restrictions: https://bugs.webkit.org/show_bug.cgi?id=222262
        clipboard.write([
            new ClipboardItem({
                "text/plain": new Promise((resolve) => {
                    lastValueFrom(
                        shareWishlistsService.shareWishlists(
                            channelName,
                            wishlistIds
                        )
                    ).then(({ shareWishlistsUrl }) => {
                        resolve(
                            new Blob([shareWishlistsUrl], {
                                type: "text/plain",
                            })
                        );

                        linkCopiedCallback();
                    });
                }),
            }),
        ]);
    };

    const handleOpenPopup = async (
        channelName: WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES,
        wishlistIds: number[]
    ) => {
        const { shareWishlistsUrl } = await lastValueFrom(
            shareWishlistsService.shareWishlists(channelName, wishlistIds)
        );

        if (isMobile) {
            windowPopupManager.open(shareWishlistsUrl, "_self");
        } else {
            windowPopupManager.open(shareWishlistsUrl);
        }
    };

    const handleSortItems = (value: WISHLIST_ITEMS_SORTING_TYPE) => {
        wishlistSortingManager.setCurrentSortValue(value);
    };

    const handleFilterItems = (value: WISHLIST_ITEMS_FILTERING_TYPE) => {
        wishlistFilterManager.setFilter([value]);
    };

    const handleApproveWishlist = (e: CustomEvent) => {
        const { wishlistId } = e.detail;
        lastValueFrom(wishlistsService.approveWishlist({ wishlistId }));
    };

    const handleSendNotificationToApprovers = (e: CustomEvent) => {
        const { wishlistId } = e.detail;
        lastValueFrom(
            wishlistsService.sendNotificationToApprovers({ wishlistId })
        );
    };

    const handleCreateDraftOrder = (e: CustomEvent) => {
        const { wishlistId } = e.detail;
        lastValueFrom(wishlistsService.createDraftOrder({ wishlistId }));
    };
</script>

{#if $wishlistBranding$}
    <WishlistTheme branding={$wishlistBranding$}>
        <div class="gw-wl-page">
            <div class="gw-wl-page__header">
                <div class="gw-wl-page__header__left">
                    {#if showTitle}
                        <h3 class="gw-wl-page__header-title">
                            <T
                                namespace="wishlist.critical.WishlistPageWidget"
                                translationKey="header_title"
                            />
                        </h3>
                    {/if}

                    {#if showSaveWishlistButton}
                        <SaveWishlistButton
                            namespace="wishlist.WishlistPageWidget"
                            on:click={handleOpenSaveModal}
                        />
                    {/if}
                </div>
                {#if showSocialShareIcons}
                    <Observable
                        observable={gwI18n.selectT(
                            "wishlist.critical.WishlistPageWidget",
                            "social_share_label"
                        )}
                        let:value={t_social_share_label}
                    >
                        <Observable
                            observable={gwI18n.selectT(
                                "wishlist.critical.WishlistPageWidget",
                                "link_copied_label"
                            )}
                            let:value={t_link_copied_label}
                        >
                            <WishlistSocialShare
                                on:share={({ detail: channelName }) => {
                                    const wishlistIds = $wishlists$.map(
                                        (wishlist) => wishlist.id
                                    );

                                    handleShareWishlists(
                                        channelName,
                                        wishlistIds,
                                        changeShareLinkCopyStatus
                                    );
                                }}
                                label={t_social_share_label}
                                linkCopiedLabel={t_link_copied_label}
                                {isShareLinkCopied}
                                wishlistSocialShareChannels={$enabledSocialShareChannels$}
                                isShareLoading={$shareWishlistsStatus$ ===
                                    STATUS.LOADING}
                            />
                        </Observable>
                    </Observable>
                {/if}
            </div>

            <div class="gw-wl-page__subheader">
                <Observable
                    observable={gwI18n.selectT(
                        "wishlist.WishlistPageWidget",
                        "new_created_list_title"
                    )}
                    let:value={t_new_created_list_title}
                >
                    <Button
                        class="gw-wl-page__add-list-btn"
                        variant={BUTTON_VARIANT[
                            $wishlistBranding$.wishlistDrawerAndPageSettings
                                .buttonsStyle
                        ]}
                        on:click={() =>
                            createWishlist(t_new_created_list_title)}
                        size="large"
                        rounded
                    >
                        <slot name="icon">
                            <Icon width={10} height={10} color="#777E90">
                                <PlusIcon />
                            </Icon>
                        </slot>
                        <T
                            namespace="wishlist.critical.WishlistPageWidget"
                            translationKey="add_wishlist_button_text"
                        />
                    </Button>
                </Observable>

                <div class="gw-wl-page__filter">
                    <Sort
                        sortItems={filterItems}
                        currentSortValue={$currentFilterValues$[0]}
                        on:sortItems={(event) =>
                            handleFilterItems(event.detail.value)}
                    >
                        <T
                            namespace="wishlist.critical.WishlistPageWidget"
                            translationKey="filter_by"
                        />
                    </Sort>
                    <Sort
                        {sortItems}
                        currentSortValue={$currentSort$}
                        on:sortItems={(event) =>
                            handleSortItems(event.detail.value)}
                    >
                        <T
                            namespace="wishlist.critical.WishlistPageWidget"
                            translationKey="sort_by"
                        />
                    </Sort>
                </div>
            </div>

            {#if [STATUS.INITIAL_LOADING, STATUS.LOADING].includes($status$)}
                <WishlistPageListSkeleton />
            {:else}
                {#each $wishlists$ as wishlist (wishlist.id)}
                    <WishlistPageList
                        on:deleteWishlist={handleDeleteWishlist}
                        on:renameWishlist={handleRenameWishlist}
                        on:makeWishlistDefault={handleMakeWishlistDefault}
                        on:togglePrivateWishlist={handleTogglePrivateWishlist}
                        on:duplicateWishlist={handleDuplicateWishlist}
                        on:loadMore={handleLoadMore}
                        on:approveWishlist={handleApproveWishlist}
                        on:sendNotificationToApprovers={handleSendNotificationToApprovers}
                        on:createDraftOrder={handleCreateDraftOrder}
                        on:shareWishlists={({
                            detail: { channelName, linkCopiedCallback },
                        }) => {
                            handleShareWishlists(
                                channelName,
                                [wishlist.id],
                                linkCopiedCallback
                            );
                        }}
                        loadMoreStatus={$loadMoreStatus$}
                        {wishlist}
                        {productsQuery}
                        {combinedProductsAndProductsWithQuantityRulesQuery}
                        {modalManager}
                        {dynamicImportManager}
                        currentSort={$currentSort$}
                        currentFilter={$currentFilterValues$[0]}
                        {openedWishlistsUIStore}
                        isSharedList={false}
                        criticalNamespace="wishlist.critical.WishlistPageWidget"
                        namespace="wishlist.WishlistPageWidget"
                        wishlistSettings={$settings$}
                        enabledSocialShareChannels={$enabledSocialShareChannels$}
                        shareWishlistsStatus={$shareWishlistsStatus$}
                        sendNotificationToApproversStatus={$sendNotificationToApproversStatus$}
                        createDraftOrderStatus={$createDraftOrderStatus$}
                        approveWishlistStatus={$approveWishlistStatus$}
                        let:filteredWishlistItems
                    >
                        {#each filteredWishlistItems as item (item.productId + item.variantId)}
                            <WishlistPageItem
                                {item}
                                wishlists={$wishlists$}
                                {productsQuery}
                                {combinedProductsAndProductsWithQuantityRulesQuery}
                                {productsWithQuantityRulesQuery}
                                {wishlistsBackInStockItemsQuery}
                                {wishlistProductStatusesQuery}
                                on:addItem={() =>
                                    handleAddItem(
                                        wishlist.id,
                                        item.productId,
                                        item.variantId
                                    )}
                                on:deleteItem={() =>
                                    handleDeleteItem(
                                        wishlist.id,
                                        item.productId,
                                        item.variantId
                                    )}
                                on:moveItem={({
                                    detail: { targetWishlistId },
                                }) =>
                                    handleMoveItem(
                                        wishlist.id,
                                        item.productId,
                                        item.variantId,
                                        targetWishlistId
                                    )}
                                isSharedItem={false}
                                criticalNamespace="wishlist.critical.WishlistPageWidget"
                                namespace="wishlist.WishlistPageWidget"
                                {dynamicImportManager}
                                {isCartAvailable}
                                canEditWishlist={!isB2b ||
                                    wishlist.isOwner ||
                                    ($settings$?.isEditListsByCompanyMembersEnabled ??
                                        false)}
                                isEditProductQuantityEnabled={$settings$?.isEditProductQuantityEnabled ??
                                    true}
                            >
                                <svelte:fragment
                                    slot="options"
                                    let:productOptions
                                    let:selectedOptions
                                    let:productAndVariantInfo
                                    let:canEditWishlist
                                >
                                    {#if productOptions?.length}
                                        {#if wishlistItemOptionsEditable && canEditWishlist}
                                            <WishlistItemEditableOptions
                                                {productOptions}
                                                {selectedOptions}
                                                {productAndVariantInfo}
                                                {wishlist}
                                                wishlistItem={item}
                                                on:updateItemVariant={handleUpdateItemVariant}
                                            />
                                        {:else}
                                            <WishlistItemOptions
                                                {selectedOptions}
                                            />
                                        {/if}
                                    {/if}
                                </svelte:fragment>
                            </WishlistPageItem>
                        {/each}
                    </WishlistPageList>
                {/each}
            {/if}
            <GrowaveBrand
                class="gw-wl-page__growave-brand"
                canShowWatermark={$watermarkSettings$?.canShowWatermark}
            />
        </div>
    </WishlistTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../themes/globals.css";
    @import "../../../../themes/default/default.css";

    .gw-wl-page {
        max-width: 1240px;
        margin: 0 auto;
        word-break: normal;

        @media all and (max-width: 960px) {
            padding: 0 0.9em;
        }

        & :global(.gw-btn.gw-button) {
            border-radius: calc(
                1px *
                    var(
                        --gw-wl-wishlistDrawerAndPageSettings-buttonsBorderRadius
                    )
            ) !important;
        }

        & :global(.gw-btn.gw-button.--contained) {
            background-color: var(
                --gw-wl-wishlistDrawerAndPageSettings-buttonsColor
            );
            color: var(--gw-wl-wishlistDrawerAndPageSettings-buttonsFontColor);
            transition: all 0.3s;
            border: 0;
        }

        & :global(.gw-btn.gw-button.--outlined) {
            background-color: transparent;
            box-shadow: 0 0 0 1px
                var(--gw-wl-wishlistDrawerAndPageSettings-buttonsColor);
            color: var(--gw-wl-wishlistDrawerAndPageSettings-buttonsFontColor);
            transition: all 0.3s;
            border: 0;
        }

        &__growave-brand {
            text-align: right;
        }

        &__filter {
            display: flex;
            align-items: center;
            column-gap: 1rem;
            flex-wrap: wrap;
        }

        &__add-list-btn {
            @media screen and (max-width: 639px) {
                width: 100%;
            }
        }
    }
    .gw-wl-page__header {
        display: flex;
        flex-wrap: wrap;
        gap: 1em;
        justify-content: space-between;
        align-items: center;
        padding: 0 0 1.25rem 0;

        &__left {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        }
    }

    .gw-wl-page__subheader {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 1em;
        padding: 1em 0;

        .gw-btn.gw-button.--outlined {
            color: var(--gw-theme-main-text-primary);
            font-weight: 700;

            &:hover,
            &:focus,
            &:focus-within {
                opacity: 0.8;
                background: none;
            }
        }
    }

    .gw-wl-page__header-title {
        font-size: 22px;
        margin: 0 1.6rem 0 0;
        font-weight: 600;
    }
</style>
